import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtErr } from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { Trash2, Share2, Download, Plus, X, Copy, Check, Shield, ExternalLink, Lock } from "lucide-react";

const CATEGORIES = ["IMMEX", "USMCA", "ISO", "QUALITY", "CAPACITY", "OTHER"];

export default function Documents() {
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [showBuyerInvite, setShowBuyerInvite] = useState(false);
  const [invites, setInvites] = useState([]);
  const [err, setErr] = useState("");
  const tier = user?.subscription_tier || "free";
  const canInvite = tier === "pro" || tier === "premium";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
      if (canInvite) {
        try {
          const r = await api.get("/buyer-invites");
          setInvites(r.data);
        } catch { /* invites are a nice-to-have on this page */ }
      }
    } catch (e) {
      // Free tier (or any other paid-feature 403) — route to /pricing
      // instead of silently rendering an empty vault. The backend returns
      // a structured detail body: { upgrade_required, current_tier,
      // redirect, message }. We honor `detail.redirect` if present, else
      // default to /pricing.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail && detail.upgrade_required) {
        nav(detail.redirect || "/pricing", { replace: true });
        return;
      }
      // Other errors — surface to the user without redirecting.
      setErr(fmtErr(detail) || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function remove(id) {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/documents/${id}`);
    load();
  }

  async function download(d) {
    try {
      const { data } = await api.get(`/documents/${d.document_id}/download`);
      const link = document.createElement("a");
      link.href = `data:${data.mime_type};base64,${data.file_b64}`;
      link.download = data.file_name || d.name;
      link.click();
    } catch (e) {
      alert(fmtErr(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="container-cd py-12" data-testid="documents-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline">VAULT · COMPLIANCE</div>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[0.9]">{t("docs.title")}</h1>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)} data-testid="add-doc-btn">
          <Plus size={14} /> {t("docs.new")}
        </button>
      </div>

      {err ? <div className="cd-card p-3 mb-6 font-mono text-xs" style={{ background: "var(--red)", color: "white" }}>{err}</div> : null}

      {loading ? (
        <div className="overline">{t("common.loading")}</div>
      ) : docs.length === 0 ? (
        <div className="cd-card p-12 text-center font-mono text-sm" data-testid="docs-empty">{t("docs.empty")}</div>
      ) : (
        <div className="cd-card p-0 overflow-x-auto">
          <table className="cd-table">
            <thead>
              <tr>
                <th>Doc</th>
                <th>{t("docs.category")}</th>
                <th>{t("docs.issued")}</th>
                <th>{t("docs.expires")}</th>
                <th>File</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.document_id} data-testid={`doc-row-${d.document_id}`}>
                  <td>
                    <div className="font-semibold">{d.name}</div>
                    {d.notes ? <div className="text-xs" style={{ color: "var(--ink-2)" }}>{d.notes}</div> : null}
                  </td>
                  <td><span className="cd-chip">{d.category}</span></td>
                  <td className="font-mono text-xs">{d.issued_at || "—"}</td>
                  <td className="font-mono text-xs">
                    <ExpiryCell expires={d.expires_at} />
                  </td>
                  <td className="font-mono text-xs">
                    {d.has_file ? (
                      <button onClick={() => download(d)} className="underline" data-testid={`doc-download-${d.document_id}`}>
                        <Download size={12} className="inline" /> {d.file_name}
                      </button>
                    ) : "—"}
                  </td>
                  <td className="text-right">
                    <button className="btn btn-ghost btn-sm mr-2" onClick={() => setShareDoc(d)} data-testid={`doc-share-${d.document_id}`}>
                      <Share2 size={12} /> {t("docs.share")}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(d.document_id)} data-testid={`doc-delete-${d.document_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewDocModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} t={t} />}
      {shareDoc && <ShareModal doc={shareDoc} onClose={() => setShareDoc(null)} t={t} />}
      {showBuyerInvite && <BuyerInviteModal onClose={() => setShowBuyerInvite(false)} onCreated={() => { setShowBuyerInvite(false); load(); }} />}

      {/* CROSS-BORDER DATA BRIDGE — buyer invites */}
      <section className="mt-16 cd-card p-6" data-testid="bridge-section">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline" style={{ color: "var(--orange)" }}>
              <Shield size={11} className="inline mr-2 -mt-1" /> CROSS-BORDER DATA BRIDGE
            </div>
            <h2 className="font-display text-3xl mt-3 leading-[0.95]">Live compliance dashboards for your US buyers</h2>
            <p className="mt-3 text-sm" style={{ color: "var(--ink-2)", maxWidth: 640 }}>
              Invite your US partners to a private dashboard showing your live compliance score, document status, and expirations. They access via a private link — no login needed.
            </p>
          </div>
          {canInvite ? (
            <button className="btn btn-primary" onClick={() => setShowBuyerInvite(true)} data-testid="invite-buyer-btn">
              <Plus size={14} /> Invite a US buyer
            </button>
          ) : (
            <Link to="/pricing" className="btn btn-dark" data-testid="bridge-upgrade-link">
              <Lock size={14} /> Pro / Premium feature
            </Link>
          )}
        </div>

        {canInvite && invites.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <table className="cd-table">
              <thead>
                <tr><th>Buyer</th><th>Company</th><th>Views</th><th>Expires</th><th></th></tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.invite_id} data-testid={`invite-row-${i.invite_id}`}>
                    <td>
                      <div className="font-semibold">{i.buyer_name}</div>
                      <div className="text-xs font-mono" style={{ color: "var(--ink-2)" }}>{i.buyer_email}</div>
                    </td>
                    <td>{i.buyer_company}</td>
                    <td className="font-mono text-xs">{i.view_count}</td>
                    <td className="font-mono text-xs">{new Date(i.expires_at).toLocaleDateString()}</td>
                    <td className="text-right">
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/buyer/${i.token}`); }}
                        className="btn btn-ghost btn-sm mr-2"
                        data-testid={`invite-copy-${i.invite_id}`}
                      >
                        <Copy size={12} /> Copy link
                      </button>
                      <a href={`/buyer/${i.token}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" data-testid={`invite-preview-${i.invite_id}`}>
                        <ExternalLink size={12} /> Preview
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function BuyerInviteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", buyer_company: "", note: "", expires_in_days: 180 });
  const [err, setErr] = useState("");
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  function set(k, v) { setForm((p) => ({ ...p, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/buyer-invites", form);
      setLink(`${window.location.origin}/buyer/${data.token}`);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    }
  }

  function copy() {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(9,9,11,0.7)" }}>
      <div className="cd-card p-8 w-full max-w-lg space-y-4" data-testid="buyer-invite-modal">
        <div className="flex items-center justify-between">
          <div className="overline" style={{ color: "var(--orange)" }}>
            <Shield size={11} className="inline mr-2 -mt-1" /> INVITE A US BUYER
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        {!link ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline">Buyer name *</label>
              <input className="cd-input-box mt-2" required value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} data-testid="bi-name" />
            </div>
            <div>
              <label className="overline">Buyer email *</label>
              <input type="email" className="cd-input-box mt-2" required value={form.buyer_email} onChange={(e) => set("buyer_email", e.target.value)} data-testid="bi-email" />
            </div>
            <div>
              <label className="overline">Buyer company *</label>
              <input className="cd-input-box mt-2" required value={form.buyer_company} onChange={(e) => set("buyer_company", e.target.value)} data-testid="bi-company" />
            </div>
            <div>
              <label className="overline">Note (shown on the dashboard)</label>
              <textarea className="cd-textarea mt-2" rows={3} value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="bi-note" />
            </div>
            <div>
              <label className="overline">Expires in (days)</label>
              <input type="number" min={7} max={365} className="cd-input-box mt-2" value={form.expires_in_days} onChange={(e) => set("expires_in_days", Number(e.target.value))} data-testid="bi-days" />
            </div>
            {err ? <div className="font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }}>{err}</div> : null}
            <button type="submit" className="btn btn-primary w-full" data-testid="bi-submit">Create invite link</button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="overline" style={{ color: "var(--orange)" }}>INVITE ACTIVE — SEND THIS LINK</div>
            <div className="cd-input-box font-mono text-xs break-all">{link}</div>
            <button onClick={copy} className="btn btn-dark w-full" data-testid="bi-copy">
              {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy link</>}
            </button>
            <button onClick={() => { onCreated(); }} className="btn btn-ghost w-full">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ExpiryCell({ expires }) {
  if (!expires) return "—";
  const exp = new Date(expires);
  const now = new Date();
  const days = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
  let dotClass = "dot-green";
  if (days < 0) dotClass = "dot-red";
  else if (days < 60) dotClass = "dot-yellow";
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`dot ${dotClass}`} />
      {expires} {days >= 0 ? `(${days}d)` : "(expired)"}
    </span>
  );
}

function NewDocModal({ onClose, onCreated, t }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("IMMEX");
  const [issued, setIssued] = useState("");
  const [expires, setExpires] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("category", category);
      if (issued) fd.append("issued_at", issued);
      if (expires) fd.append("expires_at", expires);
      if (notes) fd.append("notes", notes);
      if (file) fd.append("file", file);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCreated();
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(9,9,11,0.7)" }}>
      <form onSubmit={submit} className="cd-card p-8 w-full max-w-lg space-y-4" data-testid="new-doc-modal">
        <div className="flex items-center justify-between">
          <div className="overline">NEW · DOCUMENT</div>
          <button type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div>
          <label className="overline">{t("docs.name")} *</label>
          <input className="cd-input-box mt-2" value={name} onChange={(e) => setName(e.target.value)} required data-testid="new-doc-name" />
        </div>
        <div>
          <label className="overline">{t("docs.category")} *</label>
          <select className="cd-input-box mt-2" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="new-doc-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="overline">{t("docs.issued")}</label>
            <input type="date" className="cd-input-box mt-2" value={issued} onChange={(e) => setIssued(e.target.value)} data-testid="new-doc-issued" />
          </div>
          <div>
            <label className="overline">{t("docs.expires")}</label>
            <input type="date" className="cd-input-box mt-2" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="new-doc-expires" />
          </div>
        </div>
        <div>
          <label className="overline">{t("docs.file")}</label>
          <input type="file" className="mt-2 block w-full text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="new-doc-file" />
        </div>
        <div>
          <label className="overline">{t("docs.notes")}</label>
          <textarea className="cd-textarea mt-2" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="new-doc-notes" />
        </div>
        {err ? <div className="font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }}>{err}</div> : null}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn btn-ghost">{t("common.cancel")}</button>
          <button type="submit" disabled={loading} className="btn btn-primary" data-testid="new-doc-submit">{loading ? "…" : t("common.save")}</button>
        </div>
      </form>
    </div>
  );
}

function ShareModal({ doc, onClose, t }) {
  const [days, setDays] = useState(14);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientCompany, setRecipientCompany] = useState("");
  const [note, setNote] = useState("");
  const [link, setLink] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  async function create(e) {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/share-links", {
        document_ids: [doc.document_id],
        expires_in_days: days,
        recipient_email: recipientEmail,
        recipient_company: recipientCompany,
        note,
      });
      setLink(`${window.location.origin}/share/${data.token}`);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    }
  }

  function copy() {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(9,9,11,0.7)" }}>
      <div className="cd-card p-8 w-full max-w-lg space-y-4" data-testid="share-modal">
        <div className="flex items-center justify-between">
          <div className="overline">SHARE · {doc.name.toUpperCase()}</div>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        {!link ? (
          <form onSubmit={create} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="overline">{t("share.expires_in")}</label>
                <input type="number" min={1} max={90} className="cd-input-box mt-2" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="share-days" />
              </div>
            </div>
            <div>
              <label className="overline">{t("share.recipient_email")}</label>
              <input className="cd-input-box mt-2" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="share-email" />
            </div>
            <div>
              <label className="overline">{t("share.recipient_company")}</label>
              <input className="cd-input-box mt-2" value={recipientCompany} onChange={(e) => setRecipientCompany(e.target.value)} data-testid="share-company" />
            </div>
            <div>
              <label className="overline">{t("share.note")}</label>
              <textarea className="cd-textarea mt-2" rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="share-note" />
            </div>
            {err ? <div className="font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }}>{err}</div> : null}
            <button type="submit" className="btn btn-primary w-full" data-testid="share-create-btn">{t("share.create")}</button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="overline" style={{ color: "var(--orange)" }}>LINK ACTIVE</div>
            <div className="cd-input-box font-mono text-xs break-all">{link}</div>
            <button onClick={copy} className="btn btn-dark w-full" data-testid="share-copy-btn">
              {copied ? <><Check size={14} /> {t("share.copied")}</> : <><Copy size={14} /> {t("share.copy")}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
